import '@/icons/components'
import '@/permission'
import '@/pwa/register-service-worker'

import { createApp, Directive } from 'vue'
import VueDraggableResizable from 'vue-draggable-resizable'
import VuePapaParse from 'vue-papa-parse'
import { toast } from 'vue-sonner'
import { createApolloProvider } from '@vue/apollo-option'

// import 'element-plus/theme-chalk/index.css'
import * as Sentry from '@sentry/vue'
import { VueSvgIconPlugin } from '@yzfe/vue-svgicon'
import ElementPlus from 'element-plus'
import { createPinia } from 'pinia'

import App from '@/App.vue'
import * as directives from '@/directives'
import i18n from '@/lang/'
import { setupQuery } from '@/plugins/query'
import { router } from '@/router'
import { themeInit } from '@/theming'
import moment from '@/utils/moment'

import apolloClient from './utils/apollo'

import 'normalize.css'
import '@/styles/base.scss'
import '@/styles/element-variables.scss'
import '@/styles/index.scss'
import './styles/custom-icons.css'
import '@yzfe/svgicon/lib/svgicon.css'

const pinia = createPinia()
pinia.use(Sentry.createSentryPiniaPlugin())

const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
  defaultOptions: {
    $query: {
      fetchPolicy: 'cache-and-network',
    },
  },
})

const app = createApp(App)
app.use(apolloProvider)

app.use(i18n)
app.use(moment)
app.use(VuePapaParse)

// this part resolve an issue where the markers would not appear
// delete Icon.Default.prototype._getIconUrl
//
// Icon.Default.mergeOptions({
//   iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
//   iconUrl: require('leaflet/dist/images/marker-icon.png'),
//   shadowUrl: require('leaflet/dist/images/marker-shadow.png')
// })

// app.use(VueApollo)

app.use(ElementPlus, {
  // size: AppModule.size, // Set element-ui default size
  locale: i18n.global.messages.value[i18n.global.locale.value].element,
})

app.use(VueSvgIconPlugin, {
  tagName: 'svg-icon',
})

app.component('VueDraggableResizable', VueDraggableResizable)

// Register global directives
Object.keys(directives).forEach(key => {
  app.directive(key, (directives as Record<string, Directive>)[key])
})

themeInit()

if (import.meta.env.VITE_APP_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_SENTRY_ENVIRONMENT,
    debug: import.meta.env.NODE_ENV === 'development',
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
        },
      }),
    ],
    enableLogs: true,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: Number.parseInt(
      (import.meta.env.VITE_APP_SENTRY_TRACE_RATE || 0.5).toString(),
    ),
    tracePropagationTargets: [
      'localhost',
      `${import.meta.env.VITE_APP_PUBLIC_API_HTTP_URL}`,
      /^\//,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}
// new Vue({
//   router,
//   store,
//   i18n: i18n,
//   apolloProvider,
//   render: h => h(App),
// }).$mount('#app')

app.config.errorHandler = (err, vm, info) => {
  console.error('Error:', err)
  console.error('Vue component:', vm)
  console.error('Additional info:', info)

  Sentry.captureException(err, {
    extra: { info },
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const t = (i18n.global as any).t as (key: string) => string
  toast.error(t('generic.errorGlobalTitle'), {
    description: t('generic.errorGlobalDescription'),
    duration: Infinity,
    action: {
      label: t('generic.errorReloadPage'),
      onClick: () => window.location.reload(),
    },
  })
}

// https://vite.dev/guide/build.html#load-error-handling
window.addEventListener('vite:preloadError', _ => {
  window.location.reload()
})

app.use(pinia)
app.use(router)

// TanStack Query for oRPC API v2 (runs alongside Apollo during migration)
setupQuery(app)

// Configure generated SDK client with auth headers
import { setupSdkClient } from '@/api/v2/sdk-client'
setupSdkClient()

app.mount('#app')
